import { z } from 'zod';
import { messageKey } from './messages.js';
/**
 * Файловый обмен поверх расширения TightVNC (§8.2, §11 фаза 6).
 *
 * Протокол целиком живёт в `access-gateway` — здесь только то, что видят
 * браузер и `api`: записи каталога, параметры операций и правила путей.
 * Разбор формата сообщений — `docs/tightvnc-filetransfer.md`.
 */
/** Предел на размер одного передаваемого файла по умолчанию — 2 ГиБ (задача 4). */
export const FILE_TRANSFER_DEFAULT_MAX_BYTES = 2 * 1024 * 1024 * 1024;
/**
 * Сколько передач разрешено вести одновременно в одной сессии (задача 9).
 *
 * Предел нужен не ради нагрузки на шлюз, а ради самого протокола: файловый
 * канал последовательный — один запрос, один ответ, — и параллельные
 * передачи всё равно выстроятся в очередь. Ограничение делает эту очередь
 * видимой вместо десятка «замерших» строк в интерфейсе.
 */
export const FILE_TRANSFER_MAX_CONCURRENT = 2;
/**
 * Через сколько бездействия закрывается файловый канал, миллисекунды
 * (§11 фаза 6, задача 2).
 *
 * Канал — это второе RFB-соединение к машине, и держать его открытым всю
 * сессию значит держать вторую лицензию подключения на серверах, где они
 * считаются.
 */
export const FILE_TRANSFER_IDLE_TIMEOUT_MS = 5 * 60 * 1000;
/**
 * Размер куска при передаче, байты.
 *
 * 64 КиБ — компромисс между числом обменов «запрос-ответ» (на гигабайте
 * их при таком куске шестнадцать тысяч) и объёмом, который лежит в памяти
 * шлюза одновременно. Больше 1 МиБ смысла не имеет: TightVNC читает файл
 * с диска порциями и на крупных кусках упирается в диск, а не в сеть.
 */
export const FILE_TRANSFER_CHUNK_BYTES = 64 * 1024;
/** Запись каталога — то, что §8.2 обещает в ответе `GET /sessions/:id/fs`. */
export const fileEntrySchema = z.object({
    name: z.string(),
    /** Размер в байтах. У каталога — 0. */
    size: z.number().int().nonnegative(),
    isDir: z.boolean(),
    /** Время последней правки. `null` — сервер его не сообщил (так у дисков). */
    mtime: z.iso.datetime().nullable(),
});
export const fileListSchema = z.object({
    /** Каталог, который показан. Пустая строка — список дисков. */
    path: z.string(),
    entries: z.array(fileEntrySchema),
});
// — Параметры операций ————————————————————————————————————
/**
 * Путь в запросе.
 *
 * Проверка нарочно строгая и одинаковая на всех маршрутах: путь уходит
 * на чужую машину, где его разбирает WinAPI, и всё, что можно отсечь
 * до отправки, отсекается до отправки.
 */
export const remotePathSchema = z
    .string()
    .max(4096, messageKey('validation.file.pathTooLong', { max: 4096 }))
    .refine((value) => isSafeRemotePath(value), {
    message: messageKey('validation.file.pathInvalid'),
});
export const fileListQuerySchema = z.object({
    /** Пустая строка (или отсутствие) — список дисков удалённой машины. */
    path: remotePathSchema.default(''),
});
export const filePathQuerySchema = z.object({
    path: remotePathSchema.refine((value) => value.length > 0, messageKey('validation.file.pathRequired')),
});
export const fileMkdirSchema = z.object({
    path: remotePathSchema.refine((value) => value.length > 0, messageKey('validation.file.pathRequired')),
});
/**
 * Переименование — сверх перечня §8.2.
 *
 * Расширение TightVNC его умеет (`RENAME_REQUEST`), а файловый менеджер
 * без переименования вынуждает выкачать файл и залить обратно под другим
 * именем. Обоснование — в отчёте фазы 6.
 */
export const fileRenameSchema = z.object({
    from: remotePathSchema.refine((value) => value.length > 0, messageKey('validation.file.pathRequired')),
    to: remotePathSchema.refine((value) => value.length > 0, messageKey('validation.file.newPathRequired')),
});
/**
 * Параметры выгрузки на удалённую машину.
 *
 * `transferId` присылает браузер, а не сервер: канал прогресса (§8.2,
 * `GET /sessions/:id/fs/progress/:transferId`) нужно открыть до того, как
 * начнётся сама передача, иначе первые проценты пройдут мимо. Идентификатор
 * действует внутри одной сессии, и подглядеть по нему чужую передачу нельзя:
 * сессия принадлежит одному пользователю.
 */
export const fileUploadQuerySchema = z.object({
    transferId: z.uuid(messageKey('validation.file.transferId')),
    /**
     * Полный размер файла, если браузер его знает. Нужен ради двух вещей:
     * отказать по пределу до передачи, а не на последнем байте, и показать
     * проценты, а не «передано столько-то».
     */
    size: z.coerce.number().int().nonnegative().optional(),
    /** С какого байта продолжать (§11 фаза 6, задача 6). */
    offset: z.coerce.number().int().nonnegative().default(0),
});
/** Итог выгрузки. */
export const fileUploadResultSchema = z.object({
    transferId: z.uuid(),
    path: z.string(),
    bytes: z.number().int().nonnegative(),
});
// — Прогресс выгрузки (§11 фаза 6, задача 5) ——————————————————
export const FILE_TRANSFER_STATES = ['running', 'done', 'failed'];
export const fileTransferProgressSchema = z.object({
    transferId: z.uuid(),
    state: z.enum(FILE_TRANSFER_STATES),
    path: z.string(),
    /** Передано байт. */
    sent: z.number().int().nonnegative(),
    /** Сколько всего. `null` — браузер не сообщил размер. */
    total: z.number().int().nonnegative().nullable(),
    /** Причина отказа для `failed`. */
    error: z.string().nullable(),
    errorCode: z.string().nullable(),
});
/** Сколько живёт запись о прогрессе после конца передачи, секунды. */
export const FILE_TRANSFER_PROGRESS_TTL_SEC = 300;
/** Ключ записи о прогрессе в Redis. Сессия в ключе — граница видимости. */
export function fileTransferProgressKey(sessionId, transferId) {
    return `fs:progress:${sessionId}:${transferId}`;
}
/** Ключ множества идущих передач сессии — по нему считается предел. */
export function fileTransferActiveKey(sessionId) {
    return `fs:active:${sessionId}`;
}
// — Пути ——————————————————————————————————————————————————
/**
 * Символы, которых в пути быть не может.
 *
 * Обратный слэш — потому что разделитель в протоколе один и это `/`
 * (см. `docs/tightvnc-filetransfer.md`, раздел 2). Остальные запрещены
 * самой Windows в именах файлов, а `*` и `?` вдобавок опасны: сервер
 * подставляет путь в `FindFirstFile`, где они означают образец.
 * Двоеточие разрешено ровно одно и ровно после буквы диска — проверка ниже.
 */
// Управляющие символы здесь и есть предмет проверки: перевод строки в пути
// уедет в заголовок ответа и в журнал аудита, а нулевой байт обрежет путь
// на стороне WinAPI.
// eslint-disable-next-line no-control-regex
const FORBIDDEN_CHARS = /[\\<>"|?*\u0000-\u001f]/;
/** Первый сегмент вида `C:` — буква диска Windows. */
const DRIVE_SEGMENT = /^[A-Za-z]:$/;
/**
 * Символы, недопустимые в пути POSIX.
 *
 * Их всего два вида, и оба не про имя файла: нулевой байт обрывает путь
 * на стороне сервера, управляющие символы уезжают в заголовок ответа
 * и в журнал аудита. Всё остальное — `*`, `?`, `<`, `>`, `|`, `"` и даже
 * обратный слэш — в именах файлов Linux законно, и запрещать их значило бы
 * сделать часть файлов недоступными вовсе.
 */
// eslint-disable-next-line no-control-regex
const FORBIDDEN_POSIX_CHARS = /[\u0000-\u001f]/;
/**
 * Годен ли путь к отправке на удалённую машину.
 *
 * Переходы `..` **отклоняются**, а не схлопываются. Схлопнуть их было бы
 * хуже: платформа не знает, где у сервера начинается разрешённая область —
 * её задаёт настройка TightVNC или права пользователя SSH, — и
 * «нормализованный» путь мог бы указать ровно туда, куда метил тот, кто его
 * прислал. Отказ не зависит от чужих настроек, и это его главное
 * достоинство.
 */
export function isSafeRemotePath(value, flavor = 'windows') {
    if (value.length === 0) {
        // Корень: список дисков у Windows, домашний каталог у POSIX.
        return true;
    }
    if (flavor === 'posix') {
        return isSafePosixPath(value);
    }
    if (FORBIDDEN_CHARS.test(value) || value.startsWith('/')) {
        return false;
    }
    const segments = value.split('/');
    return segments.every((segment, index) => {
        if (segment.length === 0) {
            // Пустой сегмент — это `//` внутри пути либо слэш на конце.
            return false;
        }
        if (segment === '.' || segment === '..') {
            return false;
        }
        // Двоеточие допустимо только в букве диска и только первым сегментом.
        return segment.includes(':') ? index === 0 && DRIVE_SEGMENT.test(segment) : true;
    });
}
/**
 * Путь POSIX: абсолютный, от корня.
 *
 * Относительные пути не принимаются вовсе. Относительный путь толкуется
 * сервером от текущего каталога, а текущий каталог у канала SFTP — свой,
 * и его никто не отслеживает: один и тот же запрос дважды означал бы
 * разные файлы. Абсолютный путь такой двусмысленности не имеет.
 */
function isSafePosixPath(value) {
    if (!value.startsWith('/') || FORBIDDEN_POSIX_CHARS.test(value)) {
        return false;
    }
    if (value === '/') {
        return true;
    }
    return value
        .slice(1)
        .split('/')
        .every((segment) => segment.length > 0 && segment !== '.' && segment !== '..');
}
/**
 * Приведение пути из интерфейса к протокольному виду.
 *
 * Здесь — только то, что нельзя счесть попыткой выйти за пределы: обратные
 * слэши, которые пользователь скопировал из проводника, дубли разделителей
 * и слэш на конце. Всё остальное остаётся поводом для отказа.
 */
export function normalizeRemotePath(value) {
    return value.replace(/\\/g, '/').replace(/\/+/g, '/').replace(/^\//, '').replace(/\/$/, '');
}
/** Путь дочернего элемента: `join('C:/Обмен', 'файл.txt')`, `join('/home', 'user')`. */
export function joinRemotePath(parent, name) {
    if (parent.length === 0) {
        return name;
    }
    // Корень POSIX уже кончается разделителем: иначе получилось бы `//etc`.
    return parent.endsWith('/') ? `${parent}${name}` : `${parent}/${name}`;
}
/**
 * Родительский каталог. У диска родитель — корень, у корня родителя нет.
 *
 * Возвращает `null` именно для корня: интерфейсу нужно отличать «выше
 * некуда» от «выше — список дисков», иначе строка `..` в списке дисков
 * ведёт в никуда.
 */
export function parentRemotePath(value, flavor = 'windows') {
    if (value.length === 0) {
        return null;
    }
    if (flavor === 'posix') {
        // Выше корня подниматься некуда, и это не то же самое, что «выше —
        // список дисков» у Windows: там корень существует и показывается.
        if (value === '/') {
            return null;
        }
        const cut = value.lastIndexOf('/');
        return cut <= 0 ? '/' : value.slice(0, cut);
    }
    const cut = value.lastIndexOf('/');
    return cut < 0 ? '' : value.slice(0, cut);
}
/** Имя последнего сегмента: `образ.iso` из `C:/Обмен/образ.iso`. */
export function remotePathName(value) {
    const cut = value.lastIndexOf('/');
    return cut < 0 ? value : value.slice(cut + 1);
}
//# sourceMappingURL=files.js.map